import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface TenantState {
    tenantCode: string | null;
    companyName: string | null;
    ciu: string | null;
    tenantSwitchedAt: number;
    isStaleCopy: boolean;
    // The ICIdentifiantPublique of the Acomba société that was open when the user launched
    // ComptabilitéAI from the desktop. Decoded client-side from the launch src token and used
    // to detect a mismatch against the bridge's connected company. Null for portal/AcombaGo sessions.
    launchedIcIdentifiantPublique: string | null;
    // The high-water mark of the copy open at desktop launch (decoded from the same launch src token,
    // matching what desktop-auth resolves). Relayed on sync so the stale-copy check (SC-11) has a mark
    // to compare against / advance. Null for portal/AcombaGo sessions or when absent from the token.
    launchedHighWaterMark: number | null;
    // The licenceCIU carried by the same launch src token, alongside icIdentifiantPublique. A licence
    // transfer/re-registration changes this while icIdentifiantPublique can stay the same, so the
    // desktop fast path (DesktopAuth.tsx) also requires it to match before silently reusing a session.
    // Null for portal/AcombaGo sessions or when absent from the token.
    launchedLicenseCiu: string | null;
    // The Acomba Usager's code (UsNumero) carried by the same launch src token. A different Usager
    // opening the app under the same société/licence must not silently reuse the previous Usager's
    // session — the desktop fast path (DesktopAuth.tsx) requires this to match too before reusing one.
    // Null for portal/AcombaGo sessions, or when absent from an older desktop client's token.
    launchedUsername: string | null;
    setTenant: (tenantCode: string, companyName: string, ciu: string) => void;
    // Desktop flow: sets ciu directly from the resolved workspace — no sso/tenants call, no picker.
    setDesktopTenant: (workspaceCiu: string, companyName: string, isStaleCopy: boolean, launchedIcIdentifiantPublique?: string | null, launchedHighWaterMark?: number | null, launchedLicenseCiu?: string | null, launchedUsername?: string | null) => void;
    // Desktop fast path (DesktopAuth.tsx, tryReuseDesktopSession): re-syncs only the fields carried by
    // the fresh launch token (isStaleCopy, launchedIcIdentifiantPublique, launchedHighWaterMark,
    // launchedLicenseCiu, launchedUsername). Deliberately does NOT touch tenantCode/companyName/ciu — the
    // launch token never carries a company name, so there is nothing fresh to apply there; the previous
    // read-then-write-back of companyName/ciu via setDesktopTenant served no purpose and, if this ran
    // before persisted state had rehydrated, risked overwriting a real companyName with an empty string.
    syncDesktopLaunchContext: (isStaleCopy: boolean, launchedIcIdentifiantPublique: string | null, launchedHighWaterMark: number | null, launchedLicenseCiu: string | null, launchedUsername: string | null) => void;
    /**
     * Flips the stale-copy flag alone, leaving the launched société key, high-water mark and licence CIU
     * untouched.
     *
     * Use this rather than setDesktopTenant when only the flag changes: setDesktopTenant defaults those
     * fields to null, so calling it to clear the flag silently drops the key the company-match check needs
     * and the mark relayed on the next sync.
     *
     * Set by the desktop-auth response at launch, and cleared by a promote or an accepted sync.
     */
    setStaleCopy: (isStaleCopy: boolean) => void;
    clearTenant: () => void;
}

export const useTenantStore = create<TenantState>()(
    persist(
        (set) => ({
            tenantCode: null,
            companyName: null,
            ciu: null,
            tenantSwitchedAt: 0,
            isStaleCopy: false,
            launchedIcIdentifiantPublique: null,
            launchedHighWaterMark: null,
            launchedLicenseCiu: null,
            launchedUsername: null,
            setTenant: (tenantCode, companyName, ciu) => set({ tenantCode, companyName, ciu, tenantSwitchedAt: Date.now() }),
            setDesktopTenant: (workspaceCiu, companyName, isStaleCopy, launchedIcIdentifiantPublique = null, launchedHighWaterMark = null, launchedLicenseCiu = null, launchedUsername = null) =>
                set({ ciu: workspaceCiu, tenantCode: workspaceCiu, companyName, isStaleCopy, launchedIcIdentifiantPublique, launchedHighWaterMark, launchedLicenseCiu, launchedUsername, tenantSwitchedAt: Date.now() }),
            syncDesktopLaunchContext: (isStaleCopy, launchedIcIdentifiantPublique, launchedHighWaterMark, launchedLicenseCiu, launchedUsername) =>
                set({ isStaleCopy, launchedIcIdentifiantPublique, launchedHighWaterMark, launchedLicenseCiu, launchedUsername }),
            setStaleCopy: (isStaleCopy) => set({ isStaleCopy }),
            clearTenant: () => set({ tenantCode: null, companyName: null, ciu: null, isStaleCopy: false, launchedIcIdentifiantPublique: null, launchedHighWaterMark: null, launchedLicenseCiu: null, launchedUsername: null }),
        }),
        {
            name: 'tenant',
            version: 0,
            partialize: (s) => ({ tenantCode: s.tenantCode, companyName: s.companyName, ciu: s.ciu, isStaleCopy: s.isStaleCopy, launchedIcIdentifiantPublique: s.launchedIcIdentifiantPublique, launchedHighWaterMark: s.launchedHighWaterMark, launchedLicenseCiu: s.launchedLicenseCiu, launchedUsername: s.launchedUsername }),
        }
    )
);

export const getCiu = (): string | null => useTenantStore.getState().ciu;
